export const QUOTE_WORKFLOW_STATUSES = {
  NewProposal: 'new-proposal',
  ProposalSent: 'proposal-sent',
  ProposalInProgress: 'proposal-in-progress',
  ProposalComplete: 'proposal-complete',
  ProposalReleased: 'proposal-released',
  ReviewRequired: 'review-required',
  QuoteRequested: 'quote-requested',
  QuoteOnHold: 'quote-on-hold',
  QuoteHE: 'quote-he', // A FE Status for Norway Only to be replaced eventually
  QuoteReady: 'quote-ready',
  QuoteSent: 'quote-sent',
  QuoteAccepted: 'quote-accepted',
  AwaitingPayment: 'awaiting-payment',
  PaymentProcessing: 'payment-processing',
  PaymentReceived: 'payment-received',
  PolicyDocumentationSent: 'policy-documentation-sent',
  Lapsed: 'lapsed',
  NotTakenUp: 'not-taken-up',
} as const;

export type QuoteWorkflowStatus =
  (typeof QUOTE_WORKFLOW_STATUSES)[keyof typeof QUOTE_WORKFLOW_STATUSES];

const QUOTE_WORKFLOW_STATUSES_ORDER: Record<
  QuoteWorkflowStatus,
  number | null
> = {
  'new-proposal': 1,
  'proposal-sent': 2,
  'proposal-in-progress': 3,
  'proposal-released': 4,
  'proposal-complete': 5,
  'review-required': 6,
  'quote-requested': 7,
  'quote-on-hold': 8,
  'quote-he': 8.5, // A FE Status for Norway Only to be replaced eventually
  'quote-ready': 9,
  'quote-sent': 10,
  'quote-accepted': 11,
  'awaiting-payment': 12,
  'payment-processing': 13,
  'payment-received': 14,
  'policy-documentation-sent': 15,
  lapsed: 16,
  'not-taken-up': 17,
};

export function isQuoteWorkflowStatusBefore(
  status: QuoteWorkflowStatus | undefined,
  statusToCompare: QuoteWorkflowStatus
): boolean {
  if (!status) {
    return false;
  }

  const statusIndexes: number[] = getStatusIndexes(status, statusToCompare);

  if (statusIndexes.length < 2) {
    return false;
  }

  return statusIndexes[0] < statusIndexes[1];
}

export function isQuoteWorkflowStatusBeforeOrEqual(
  status: QuoteWorkflowStatus | undefined,
  statusToCompare: QuoteWorkflowStatus
): boolean {
  if (!status) {
    return false;
  }

  const statusIndexes: number[] = getStatusIndexes(status, statusToCompare);

  if (statusIndexes.length < 2) {
    return false;
  }

  return statusIndexes[0] <= statusIndexes[1];
}

export function isQuoteWorkflowStatusAfterOrEqual(
  status: QuoteWorkflowStatus | undefined,
  statusToCompare: QuoteWorkflowStatus
): boolean {
  if (!status) {
    return false;
  }

  const statusIndexes: number[] = getStatusIndexes(status, statusToCompare);

  if (statusIndexes.length < 2) {
    return false;
  }

  return statusIndexes[0] >= statusIndexes[1];
}

export function isQuoteWorkflowStatusBetween(
  status: QuoteWorkflowStatus,
  beginStatus: QuoteWorkflowStatus,
  endStatus: QuoteWorkflowStatus
): boolean {
  return (
    isQuoteWorkflowStatusAfterOrEqual(status, beginStatus) &&
    isQuoteWorkflowStatusBeforeOrEqual(status, endStatus)
  );
}

function getStatusIndexes(
  status: QuoteWorkflowStatus,
  statusToCompare: QuoteWorkflowStatus
): number[] {
  const statusIndex = QUOTE_WORKFLOW_STATUSES_ORDER[status];
  const statusToCompareIndex = QUOTE_WORKFLOW_STATUSES_ORDER[statusToCompare];

  if (!statusIndex || !statusToCompareIndex) {
    return [];
  }

  return [statusIndex, statusToCompareIndex];
}
